import { useEffect, useState } from "react";

export interface NetworkStatus {
  connected: boolean;
}

/**
 * Hook for monitoring network connectivity status via the browser online/offline events.
 *
 * @example
 * ```tsx
 * const { connected } = useNetworkStatus();
 * if (!connected) return <div>No internet connection</div>;
 * ```
 */
export function useNetworkStatus(): NetworkStatus {
  const [connected, setConnected] = useState<boolean>(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    const handleOnline = () => setConnected(true);
    const handleOffline = () => setConnected(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return { connected };
}
